<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <div class="leave-requests-container py-3">
        <v-skeleton-loader
          v-if="loadingAttachmentRequired"
          type="card, card"
          class="mb-4 mx-4"
        ></v-skeleton-loader>

        <div
          v-else-if="
            !attachmentRequiredRequests ||
            attachmentRequiredRequests.length === 0
          "
          class="text-center py-8"
        >
          <v-icon size="64" color="grey lighten-1">mdi-attachment</v-icon>
          <div class="text-h6 text-h4 mt-2">沒有銷假紀錄</div>
          <div class="text-body-2 text-h4">所有銷假紀錄都有附件</div>
        </div>

        <template v-else>
          <v-card
            v-for="request in attachmentRequiredRequests"
            :key="request.id"
            class="mb-4 mx-4"
            outlined
          >
            <v-card-text class="pa-0">
              <div class="d-flex align-center px-4 pt-4">
                <div style="flex: 1; flex-basis: 0" id="applicant">
                  <div class="d-flex align-center">
                    <span class="font-weight-medium text-h5">{{
                      request.applicant.name
                    }}</span>
                    <v-chip
                      x-medium
                      label
                      :color="REVIEW_STATUS_COLOR[request.review_status]"
                      text-color="white"
                      class="ml-2 px-2"
                    >
                      {{ REVIEW_STATUS_TEXT[request.review_status] }}
                    </v-chip>
                  </div>
                  <div
                    class="text-subtitle-2 text-h4 font-weight-bold red--text"
                  >
                    <span>{{ getLeaveTypeText(request) }}</span>
                  </div>
                </div>
                <div
                  id="abnormal"
                  style="flex: 1; flex-basis: 0"
                  class="text-center"
                >
                  <span
                    class="text-h5 red--text"
                    v-if="
                      request.abnormal_status != null &&
                      request.abnormal_status > 0
                    "
                  >
                    異常:
                    {{
                      ABNORMAL_STATUS[request.abnormal_status] ||
                      request.abnormal_status
                    }}
                  </span>
                </div>
                <div id="date" style="flex: 1; flex-basis: 0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-h5 mr-2">
                      申請日期:
                      <formatted-date :date="request.create_time" />
                    </span>
                    <template v-if="canEdit">
                      <template
                        v-if="
                          !editingRequest || editingRequest.id !== request.id
                        "
                      >
                        <v-btn icon @click="startEdit(request)" :ripple="false">
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-btn
                          icon
                          class="mr-2"
                          @click="cancelEdit"
                          :ripple="false"
                        >
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          :loading="saving"
                          @click="saveEdit(request)"
                          :ripple="false"
                        >
                          <v-icon color="red">mdi-content-save</v-icon>
                        </v-btn>
                      </template>
                    </template>
                  </div>
                </div>
              </div>

              <v-row class="px-4 py-0 my-0" v-if="request.apply_type != 4">
                <v-col cols="4">
                  <div class="text-h6">開始時間:</div>
                  <div>
                    <formatted-date :date="request.start_time" />
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="text-h6">結束時間:</div>
                  <div>
                    <formatted-date :date="request.end_time" />
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="text-h6">期間:</div>
                  <div>
                    {{ formatMinutesToDaysString(request.leave_minutes) }}
                  </div>
                </v-col>
              </v-row>
              <v-row class="px-4 py-0 my-0" v-else-if="request.extra">
                <v-col cols="4" v-if="request.extra.clock_in_actual">
                  <div class="text-h6">實際上班打卡:</div>
                  <div>
                    {{ formatDateTime(request.extra.clock_in_actual) }}
                  </div>
                </v-col>
                <v-col cols="4" v-if="request.extra.clock_out_actual">
                  <div class="text-h6">實際下班打卡:</div>
                  <div>
                    {{ formatDateTime(request.extra.clock_out_actual) }}
                  </div>
                </v-col>
                <v-col cols="4" v-if="request.extra.lunch_break_start_actual">
                  <div class="text-h6">實際午休開始:</div>
                  <div>
                    {{ formatDateTime(request.extra.lunch_break_start_actual) }}
                  </div>
                </v-col>
                <v-col cols="4" v-if="request.extra.lunch_break_end_actual">
                  <div class="text-h6">實際午休結束:</div>
                  <div>
                    {{ formatDateTime(request.extra.lunch_break_end_actual) }}
                  </div>
                </v-col>
                <v-col cols="4" v-if="request.extra.rest_1_start_actual">
                  <div class="text-h6">實際休息一開始:</div>
                  <div>
                    {{ formatDateTime(request.extra.rest_1_start_actual) }}
                  </div>
                </v-col>
                <v-col cols="4" v-if="request.extra.rest_1_end_actual">
                  <div class="text-h6">實際休息一結束:</div>
                  <div>
                    {{ formatDateTime(request.extra.rest_1_end_actual) }}
                  </div>
                </v-col>
              </v-row>
              <v-row class="px-4 py-0 my-0">
                <v-col cols="4">
                  <div class="text-h6">事由:</div>
                  <div>
                    <span>{{ request.reason || "-" }}</span>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="text-h6">附件:</div>
                  <div
                    v-if="!editingRequest || editingRequest.id !== request.id"
                  >
                    <div
                      v-if="
                        request.attachmentFiles &&
                        request.attachmentFiles.length
                      "
                    >
                      <div class="d-flex flex-wrap">
                        <a
                          v-for="(file, idx) in request.attachmentFiles"
                          :key="idx"
                          class="mr-2"
                          :href="buildAttachmentHref(file)"
                          target="_blank"
                          rel="noopener"
                        >
                          {{
                            typeof file === "string"
                              ? "附件" + (idx + 1)
                              : file.name || "附件" + (idx + 1)
                          }}
                        </a>
                      </div>
                    </div>
                    <div v-else-if="request.attachment">
                      <a
                        :href="
                          typeof request.attachment === 'string'
                            ? request.attachment
                            : request.attachment.url || request.attachment.link
                        "
                        target="_blank"
                        rel="noopener"
                      >
                        {{
                          typeof request.attachment === "string"
                            ? "附件"
                            : request.attachment.name || "附件"
                        }}
                      </a>
                    </div>
                    <div v-else-if="request.attachment_url">
                      <a
                        :href="request.attachment_url"
                        target="_blank"
                        rel="noopener"
                        >附件</a
                      >
                    </div>
                    <div v-else>-</div>
                  </div>
                  <v-file-input
                    v-else
                    v-model="editingRequest.newAttachments"
                    label="Upload Attachments"
                    dense
                    multiple
                  ></v-file-input>
                </v-col>
                <v-col cols="4">
                  <div class="text-h6">拒絕原因:</div>
                  <div>
                    <span>{{ request.reject_reason || "-" }}</span>
                  </div>
                </v-col>
              </v-row>
              <v-row
                class="px-4 py-0 my-0"
                v-if="
                  request.apply_type === 2 &&
                  request.extra &&
                  request.extra.reward_selection
                "
              >
                <v-col cols="4">
                  <div class="text-h6">加班補償:</div>
                  <div>
                    <span>{{ getRewardSelectionDisplay(request.extra) }}</span>
                  </div>
                </v-col>
              </v-row>
              <!-- row for extra -->
              <v-row
                class="px-4 py-0 my-0"
                v-if="request.extra && request.extra.status"
              >
                <v-col cols="4">
                  <div class="text-h6">狀態:</div>
                  <div>
                    <span>{{ request.extra.status }}</span>
                  </div>
                </v-col>
              </v-row>
              <div class="d-flex px-4 pb-4" v-if="canEdit">
                <v-spacer></v-spacer>
                <v-btn
                  class="
                    font-weight-bold
                    text-capitalize
                    btn-ls btn-primary
                    bg-gradient-primary
                    py-3
                    px-6
                  "
                  @click="confirmPass(request)"
                >
                  <v-icon left>mdi-check</v-icon>
                  審核通過
                </v-btn>
                <v-btn
                  class="
                    font-weight-bold
                    text-capitalize
                    btn-ls btn-danger
                    bg-gradient-danger
                    py-3
                    px-6
                    mx-2
                  "
                  @click="confirmCancel(request)"
                >
                  <v-icon left>mdi-delete</v-icon>
                  註銷假期
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <div class="d-flex justify-center py-4">
            <v-pagination
              v-model="attachmentRequiredPage"
              :length="pageCount"
              @input="fetchAttachmentRequired"
              prev-icon="fa fa-angle-left"
              next-icon="fa fa-angle-right"
              color="#cb0c9f"
              circle
            ></v-pagination>
          </div>
        </template>
      </div>
    </v-card-text>
    <confirm-dialog
      :visible.sync="confirmDialog.visible"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :yes-button-text="confirmDialog.yesButtonText"
      :no-button-text="confirmDialog.noButtonText"
      :yes-callback="confirmDialog.yesCallback"
    />
    <info-dialog
      :visible.sync="showInfoDialog"
      :title="infoDialogTitle"
      :message="infoDialogMessage"
      yes-button-text="確定"
    />
  </v-card>
</template>

<script>
import FormattedDate from "@/components/FormattedDate.vue";
import InfoDialog from "@/components/Common/InfoDialog.vue";
import {
  REVIEW_STATUS,
  REVIEW_STATUS_TEXT,
  REVIEW_STATUS_COLOR,
  LEAVE_TYPE,
  ABNORMAL_STATUS,
} from "@/constants";
import { formatDateTime as formatDateTimeUtil } from "@/utils/time";
import ConfirmDialog from "@/components/Common/ConfirmDialog.vue";

export default {
  name: "AttachmentRequiredWidget",
  computed: {
    LEAVE_TYPE_OPTIONS() {
      return Object.entries(LEAVE_TYPE).map(([id, name]) => ({
        id: Number(id),
        name,
      }));
    },
    pageCount() {
      return this.attachmentRequiredTotalPages || 1;
    },
  },
  components: {
    FormattedDate,
    InfoDialog,
    ConfirmDialog,
  },
  props: {
    selectedCompany: {
      type: Object,
      default: () => null,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      REVIEW_STATUS,
      REVIEW_STATUS_TEXT,
      REVIEW_STATUS_COLOR,
      LEAVE_TYPE,
      ABNORMAL_STATUS,
      attachmentRequiredRequests: [],
      attachmentRequiredPage: 1,
      attachmentRequiredItemsPerPage: 10,
      attachmentRequiredTotalItems: 0,
      attachmentRequiredTotalPages: 1,
      loadingAttachmentRequired: false,
      fetchError: false,
      editingRequest: null,
      saving: false,
      confirmDialog: {
        visible: false,
        title: "確認註銷",
        message: "您確定要註銷此申請嗎？此操作無法復原。",
        yesButtonText: "註銷",
        noButtonText: "取消",
        yesCallback: null,
      },
      // Info dialog
      showInfoDialog: false,
      infoDialogTitle: "",
      infoDialogMessage: "",
    };
  },
  async mounted() {
    await this.fetchAttachmentRequired();
  },
  watch: {
    selectedCompany: {
      handler() {
        this.attachmentRequiredPage = 1;
        this.fetchAttachmentRequired();
      },
      deep: true,
    },
  },
  methods: {
    async fetchAttachmentRequired() {
      this.loadingAttachmentRequired = true;
      this.attachmentRequiredRequests = [];
      this.fetchError = false;
      try {
        const params = new URLSearchParams({
          page: this.attachmentRequiredPage,
          limit: this.attachmentRequiredItemsPerPage,
          review_status: 2,
          review_mode: 1,
        });
        if (this.selectedCompany && this.selectedCompany.id) {
          params.append("company_id", this.selectedCompany.id);
        }
        const response = await this.$axios.get(
          `/api/applications/attachment-required?${params}`
        );
        const payload = response && response.data ? response.data : {};
        const newItems = Array.isArray(payload.data) ? payload.data : [];
        const pagination = payload.pagination || {};
        this.attachmentRequiredTotalItems =
          Number(pagination.totalItems) || newItems.length || 0;
        this.attachmentRequiredTotalPages = Number(pagination.totalPages) || 1;

        this.attachmentRequiredRequests = newItems;
      } catch (error) {
        this.attachmentRequiredRequests = [];
        this.attachmentRequiredTotalItems = 0;
        this.fetchError = true;
        this.$emit("error", {
          title: "取得需附件申請失敗",
          message:
            (error && (error.response?.data?.message || error.message)) ||
            "請稍後再試",
        });
      } finally {
        this.loadingAttachmentRequired = false;
      }
    },
    getRewardSelectionDisplay(extra) {
      if (!extra) return "-";
      const raw = extra.reward_selection;
      if (raw == null) return "-";

      const labelMap = {
        pay: "加班費",
        comp: "補休假",
      };

      const toLabels = (items) =>
        items
          .map((s) => String(s).trim())
          .filter((s) => s.length)
          .map((code) => labelMap[code] || code);

      if (Array.isArray(raw)) return toLabels(raw).join(", ");

      if (typeof raw === "string") {
        const trimmed = raw.trim();
        // Example: "[pay, comp]" => ["pay","comp"]
        if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
          const inner = trimmed.slice(1, -1);
          const parts = inner
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length);
          return toLabels(parts).join(", ");
        }
        // Example: "pay, comp" or "pay" => map to labels
        const parts = trimmed
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length);
        return toLabels(parts).join(", ");
      }

      return labelMap[String(raw)] || String(raw);
    },
    formatMinutesToDaysString(minutes) {
      const mins = Number(minutes);
      if (!Number.isFinite(mins)) {
        return "0天0小時";
      }

      const totalHours = mins / 60;
      const dayHours = 8;
      const days = Math.floor(totalHours / dayHours);
      const remainingHours = totalHours - days * dayHours;

      const formatValue = (value) => {
        if (Math.abs(value - Math.round(value)) < 1e-9) {
          return String(Math.round(value));
        }
        return value.toFixed(2);
      };

      return `${days}天${formatValue(Math.max(remainingHours, 0))}小時`;
    },
    buildAttachmentHref(file) {
      if (!file) return "#";

      const normalize = (value) => {
        if (!value && value !== 0) return null;
        let stringValue = typeof value === "string" ? value : String(value);
        stringValue = stringValue.trim();
        if (!stringValue) return null;
        if (/^https?:\/\//i.test(stringValue)) {
          return stringValue;
        }

        const base = (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");

        const normalizedValue = stringValue.replace(/^\/+/, "");
        if (normalizedValue.startsWith("resource/attachments/")) {
          return `${base}/${normalizedValue}`;
        }

        return `${base}/resource/attachments/${normalizedValue}`;
      };

      if (typeof file === "string") {
        const href = normalize(file);
        return href || "#";
      }

      const direct = normalize(file.url || file.link);
      if (direct) {
        return direct;
      }

      const identifier =
        file.filename || file.file_name || file.file || file.id;

      const href = normalize(identifier);
      return href || "#";
    },
    formatDateTime(value) {
      return formatDateTimeUtil(value);
    },

    getLeaveTypeText(request) {
      let leave_type = request.leave_type;
      let apply_type = request.apply_type;
      //  apply_type   comment: '1:請假申請 2:加班申請 3:調班申請 4:補打卡申請 5:出差申請 6:外出申請 7:離職申請'
      switch (apply_type) {
        case 1:
          return LEAVE_TYPE[leave_type] || leave_type;
        case 2:
          return "加班申請";
        case 3:
          return "調班申請";
        case 4:
          return "補打卡申請";
        case 5:
          return "出差申請";
        case 6:
          return "外出申請";
        case 7:
          return "離職申請";
        default:
          return "請假申請";
      }
    },
    startEdit(request) {
      this.editingRequest = { ...request, newAttachments: [] };
    },
    cancelEdit() {
      this.editingRequest = null;
    },
    async saveEdit(request) {
      this.saving = true;
      try {
        const payload = {
          leave_type: this.editingRequest.leave_type,
        };
        // Handle file uploads
        if (
          this.editingRequest.newAttachments &&
          this.editingRequest.newAttachments.length
        ) {
          const formData = new FormData();
          this.editingRequest.newAttachments.forEach((file) =>
            formData.append("files", file)
          );
          const uploadRes = await this.$axios.post(
            `/api/applications/${this.editingRequest.id}/attachments`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          payload.attachmentFiles = uploadRes.data.data; // array of attachment objects
        }
        await this.$axios.put(
          `/api/applications/${this.editingRequest.id}`,
          payload
        );
        // Update local
        const index = this.attachmentRequiredRequests.findIndex(
          (r) => r.id === request.id
        );
        if (index !== -1) {
          this.$set(
            this.attachmentRequiredRequests[index],
            "leave_type",
            this.editingRequest.leave_type
          );
          if (payload.attachmentFiles) {
            this.$set(
              this.attachmentRequiredRequests[index],
              "attachmentFiles",
              payload.attachmentFiles
            );
          }
        }
        this.editingRequest = null;
        // Emit success
      } catch (error) {
        this.$emit("error", {
          title: "更新失敗",
          message: error.response?.data?.message || "更新時發生錯誤",
        });
      } finally {
        this.saving = false;
      }
    },
    confirmPass(request) {
      if (!request || !request.id) return;
      this.confirmDialog.title = "確認通過";
      this.confirmDialog.message = "您確定要通過此申請嗎？";
      this.confirmDialog.yesButtonText = "通過";
      this.confirmDialog.noButtonText = "取消";
      this.confirmDialog.yesCallback = async () => {
        try {
          await this.$axios.post(`/api/applications/${request.id}/pass`);
          // Refresh list since the item should no longer require attachment
          await this.fetchAttachmentRequired();
          // Show success message
          this.infoDialogTitle = "審核成功";
          this.infoDialogMessage = "申請已通過審核";
          this.showInfoDialog = true;
        } catch (error) {
          this.$emit("error", {
            title: "通過失敗",
            message:
              error?.response?.data?.message || error?.message || "請稍後再試",
          });
        } finally {
          this.confirmDialog.yesCallback = null;
        }
      };
      this.confirmDialog.visible = true;
    },
    confirmCancel(request) {
      if (!request || !request.id) return;
      this.confirmDialog.title = "確認註銷";
      this.confirmDialog.message = "您確定要註銷此申請嗎？此操作無法復原。";
      this.confirmDialog.yesButtonText = "註銷";
      this.confirmDialog.noButtonText = "取消";
      this.confirmDialog.yesCallback = async () => {
        try {
          await this.$axios.post(`/api/applications/${request.id}/cancel`);
          // Refresh list since the item should no longer require attachment
          await this.fetchAttachmentRequired();
          // Show success message
          this.infoDialogTitle = "註銷成功";
          this.infoDialogMessage = "申請已成功註銷";
          this.showInfoDialog = true;
        } catch (error) {
          this.$emit("error", {
            title: "註銷失敗",
            message:
              error?.response?.data?.message || error?.message || "請稍後再試",
          });
        } finally {
          this.confirmDialog.yesCallback = null;
        }
      };
      this.confirmDialog.visible = true;
    },
  },
};
</script>
